import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Droplets, Wheat, Coffee, Fuel, Wifi, WifiOff } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, AreaChart, Area } from "recharts";
import api from "@/lib/api";

// Initial commodity data
const INITIAL_COMMODITIES = {
  soft: [
    { id: "cocoa", name: "Cocoa", price: 3974.00, change: 2.36, unit: "$/ton", icon: "🍫" },
    { id: "coffee", name: "Coffee", price: 2994.00, change: -1.24, unit: "$/ton", icon: "☕" },
    { id: "sugar", name: "Sugar", price: 17.42, change: 0.44, unit: "$/lb", icon: "🍬" },
    { id: "cotton", name: "Cotton", price: 72.85, change: -0.82, unit: "$/lb", icon: "🧵" },
    { id: "wheat", name: "Wheat", price: 568.25, change: 1.15, unit: "$/ton", icon: "🌾" },
    { id: "maize", name: "Maize", price: 485.75, change: 0.67, unit: "$/ton", icon: "🌽" },
    { id: "soybean", name: "Soybean", price: 559.75, change: -0.35, unit: "$/ton", icon: "🫘" },
    { id: "palm_oil", name: "Palm Oil", price: 1120.00, change: 1.89, unit: "$/ton", icon: "🌴" },
  ],
  hard: [
    { id: "gold", name: "Gold", price: 2387.56, change: 2.34, unit: "$/oz", icon: "🥇" },
    { id: "crude_oil", name: "Crude Oil", price: 81.20, change: -0.95, unit: "$/barrel", icon: "🛢️" },
    { id: "copper", name: "Copper", price: 4.62, change: 1.34, unit: "$/lb", icon: "🔶" },
    { id: "natural_gas", name: "Natural Gas", price: 2.38, change: 3.08, unit: "$/MMBtu", icon: "🔥" },
    { id: "silver", name: "Silver", price: 27.58, change: 0.38, unit: "$/oz", icon: "🥈" },
    { id: "zinc", name: "Zinc", price: 2929.00, change: 1.79, unit: "$/ton", icon: "⚪" },
  ]
};

const ICONS = {
  cocoa: "🍫", coffee: "☕", sugar: "🍬", cotton: "🧵", wheat: "🌾", 
  maize: "🌽", soybean: "🫘", palm_oil: "🌴", gold: "🥇", crude_oil: "🛢️",
  copper: "🔶", natural_gas: "🔥", silver: "🥈", zinc: "⚪"
};

const generatePriceHistory = (basePrice, volatility = 0.02) => {
  return Array.from({ length: 30 }, (_, i) => ({
    day: i + 1,
    price: basePrice * (1 + (Math.random() - 0.5) * volatility * 2),
  }));
};

export default function MarketOverviewPage() {
  const [commodities, setCommodities] = useState(INITIAL_COMMODITIES);
  const [selectedCommodity, setSelectedCommodity] = useState(INITIAL_COMMODITIES.soft[0]);
  const [marketSentiment, setMarketSentiment] = useState(67);
  const [topGainers, setTopGainers] = useState([]);
  const [topLosers, setTopLosers] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [flashingPrices, setFlashingPrices] = useState({});
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/api/ws/prices`);
    
    ws.onopen = () => {
      console.log("Price stream connected");
      setWsConnected(true);
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === "init") {
        // Initialize prices from server
        const serverPrices = data.prices;
        setCommodities(prev => {
          const newSoft = prev.soft.map(c => ({
            ...c,
            price: serverPrices[c.id]?.price ?? c.price,
            change: serverPrices[c.id]?.change ?? c.change,
          }));
          const newHard = prev.hard.map(c => ({
            ...c,
            price: serverPrices[c.id]?.price ?? c.price,
            change: serverPrices[c.id]?.change ?? c.change,
          }));
          return { soft: newSoft, hard: newHard };
        });
        setLastUpdate(new Date());
      } 
      else if (data.type === "tick") {
        // Update prices from tick
        const updates = data.updates;
        const flashIds = {};
        
        setCommodities(prev => {
          const newSoft = prev.soft.map(c => {
            const update = updates.find(u => u.id === c.id);
            if (update) {
              flashIds[c.id] = update.tick >= 0 ? "up" : "down";
              return { ...c, price: update.price, change: update.change };
            }
            return c;
          });
          const newHard = prev.hard.map(c => {
            const update = updates.find(u => u.id === c.id);
            if (update) {
              flashIds[c.id] = update.tick >= 0 ? "up" : "down";
              return { ...c, price: update.price, change: update.change };
            }
            return c;
          });
          return { soft: newSoft, hard: newHard };
        });
        
        // Update selected commodity if it was updated
        const selectedUpdate = updates.find(u => u.id === selectedCommodity.id);
        if (selectedUpdate) {
          setSelectedCommodity(prev => ({
            ...prev,
            price: selectedUpdate.price,
            change: selectedUpdate.change
          }));
        }
        
        // Flash effect
        setFlashingPrices(flashIds);
        setTimeout(() => setFlashingPrices({}), 300);
        setLastUpdate(new Date());
      }
    };
    
    ws.onclose = () => {
      console.log("Price stream disconnected");
      setWsConnected(false);
      // Reconnect after 3 seconds
      reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000);
    };
    
    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };
    
    wsRef.current = ws;
  }, [selectedCommodity.id]);

  useEffect(() => {
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connectWebSocket]);

  // Update top gainers/losers when commodities change
  useEffect(() => {
    const allCommodities = [...commodities.soft, ...commodities.hard];
    const sorted = [...allCommodities].sort((a, b) => b.change - a.change);
    setTopGainers(sorted.filter(c => c.change > 0).slice(0, 4));
    setTopLosers(sorted.filter(c => c.change < 0).slice(0, 4));
    
    // Update market sentiment based on gainers vs losers
    const gainersCount = allCommodities.filter(c => c.change > 0).length;
    setMarketSentiment(Math.round((gainersCount / allCommodities.length) * 100));
  }, [commodities]);

  const priceHistory = generatePriceHistory(selectedCommodity.price);

  return (
    <div data-testid="market-overview-page" className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase tracking-wider text-zinc-100">
            Good morning!
          </h1>
          <p className="text-xs text-zinc-500 font-mono mt-1">
            Africa Market Overview | Real-time commodities intelligence
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* WebSocket Status */}
          <div className="flex items-center gap-2">
            {wsConnected ? (
              <>
                <Wifi className="w-4 h-4 text-[#00ff41]" />
                <span className="text-[10px] text-[#00ff41] font-mono uppercase">Live</span>
              </>
            ) : (
              <>
                <WifiOff className="w-4 h-4 text-[#ff3b30]" />
                <span className="text-[10px] text-[#ff3b30] font-mono uppercase">Offline</span>
              </>
            )}
          </div>
          <div className="text-right">
            <p className="text-[10px] text-zinc-500 uppercase">Global Sentiment</p>
            <p className={`text-lg font-mono font-bold ${marketSentiment >= 50 ? "text-[#00ff41]" : "text-[#ff3b30]"}`}>
              {marketSentiment}% Bullish
            </p>
          </div>
        </div>
      </div>

      {/* Soft Commodities Row */}
      <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
        <CardHeader className="pb-2 px-4 pt-4">
          <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
            <Wheat className="w-3.5 h-3.5 text-[#eab308]" />
            Soft Commodities
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {commodities.soft.map((c) => (
              <CommodityCard
                key={c.id}
                commodity={c}
                selected={selectedCommodity.id === c.id}
                onClick={() => setSelectedCommodity(c)}
                flashing={flashingPrices[c.id]}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Hard Commodities Row */}
      <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
        <CardHeader className="pb-2 px-4 pt-4">
          <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
            <Fuel className="w-3.5 h-3.5 text-[#f97316]" />
            Hard Commodities
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {commodities.hard.map((c) => (
              <CommodityCard
                key={c.id}
                commodity={c}
                selected={selectedCommodity.id === c.id}
                onClick={() => setSelectedCommodity(c)}
                flashing={flashingPrices[c.id]}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Price Chart and Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Main Price Chart */}
        <Card className="lg:col-span-2 bg-zinc-950/50 border-zinc-800 rounded-none">
          <CardHeader className="pb-2 px-4 pt-4">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-sm uppercase tracking-wider text-zinc-300 flex items-center gap-3">
                <span className="text-xl">{ICONS[selectedCommodity.id] || "📊"}</span>
                {selectedCommodity.name}
                <Badge variant="outline" className={`rounded-none text-[10px] ${selectedCommodity.change >= 0 ? "border-[#00ff41] text-[#00ff41]" : "border-[#ff3b30] text-[#ff3b30]"}`}>
                  {selectedCommodity.change >= 0 ? "+" : ""}{selectedCommodity.change.toFixed(2)}%
                </Badge>
              </CardTitle>
              <div className="text-right">
                <p className={`text-2xl font-mono font-bold transition-colors duration-300 ${
                  flashingPrices[selectedCommodity.id] === "up" ? "text-[#00ff41]" :
                  flashingPrices[selectedCommodity.id] === "down" ? "text-[#ff3b30]" : "text-zinc-100"
                }`}>
                  ${selectedCommodity.price.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </p>
                <p className="text-[10px] text-zinc-500 font-mono">{selectedCommodity.unit}</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={priceHistory}>
                <defs>
                  <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={selectedCommodity.change >= 0 ? "#00ff41" : "#ff3b30"} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={selectedCommodity.change >= 0 ? "#00ff41" : "#ff3b30"} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" tick={{ fill: "#71717a", fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis 
                  tick={{ fill: "#71717a", fontSize: 10 }} 
                  axisLine={false} 
                  tickLine={false} 
                  domain={['auto', 'auto']}
                  tickFormatter={(v) => `$${v.toFixed(0)}`}
                />
                <RechartsTooltip 
                  contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 0, fontSize: 11, fontFamily: "JetBrains Mono" }} 
                  formatter={(v) => [`$${v.toFixed(2)}`, "Price"]}
                />
                <Area 
                  type="monotone" 
                  dataKey="price" 
                  stroke={selectedCommodity.change >= 0 ? "#00ff41" : "#ff3b30"} 
                  fill="url(#priceGrad)" 
                  strokeWidth={2} 
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Movers */}
        <div className="space-y-4">
          {/* Top Gainers */}
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <TrendingUp className="w-3.5 h-3.5 text-[#00ff41]" />
                Top Gainers
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2">
              {topGainers.map((c) => (
                <div key={c.id} className={`flex items-center justify-between py-1.5 border-b border-zinc-900 transition-colors duration-300 ${
                  flashingPrices[c.id] === "up" ? "bg-[#00ff41]/10" : ""
                }`}>
                  <div className="flex items-center gap-2">
                    <span>{ICONS[c.id]}</span>
                    <span className="text-xs text-zinc-300 font-mono">{c.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-mono text-zinc-300">${c.price.toLocaleString()}</p>
                    <p className="text-[10px] font-mono text-[#00ff41]">+{c.change.toFixed(2)}%</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Top Losers */}
          <Card className="bg-zinc-950/50 border-zinc-800 rounded-none">
            <CardHeader className="pb-2 px-4 pt-4">
              <CardTitle className="font-heading text-xs uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                <TrendingDown className="w-3.5 h-3.5 text-[#ff3b30]" />
                Top Losers
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2">
              {topLosers.map((c) => (
                <div key={c.id} className={`flex items-center justify-between py-1.5 border-b border-zinc-900 transition-colors duration-300 ${
                  flashingPrices[c.id] === "down" ? "bg-[#ff3b30]/10" : ""
                }`}>
                  <div className="flex items-center gap-2">
                    <span>{ICONS[c.id]}</span>
                    <span className="text-xs text-zinc-300 font-mono">{c.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-mono text-zinc-300">${c.price.toLocaleString()}</p>
                    <p className="text-[10px] font-mono text-[#ff3b30]">{c.change.toFixed(2)}%</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Last Update */}
          {lastUpdate && (
            <div className="text-center">
              <p className="text-[9px] text-zinc-600 font-mono">
                Last update: {lastUpdate.toLocaleTimeString()}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CommodityCard({ commodity, selected, onClick, flashing }) {
  return (
    <button
      onClick={onClick}
      className={`p-3 text-left border transition-all duration-100 ${
        selected 
          ? "bg-zinc-900 border-[#00ff41]" 
          : "bg-zinc-950/30 border-zinc-800 hover:border-zinc-600"
      } ${
        flashing === "up" ? "!border-[#00ff41] !bg-[#00ff41]/10" :
        flashing === "down" ? "!border-[#ff3b30] !bg-[#ff3b30]/10" : ""
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm">{commodity.icon}</span>
        <span className="text-[10px] text-zinc-400 uppercase font-mono">{commodity.name}</span>
      </div>
      <p className={`text-sm font-mono font-bold transition-colors duration-300 ${
        flashing === "up" ? "text-[#00ff41]" :
        flashing === "down" ? "text-[#ff3b30]" : "text-zinc-100"
      }`}>
        ${commodity.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </p>
      <div className="flex items-center gap-1 mt-1">
        {commodity.change >= 0 ? (
          <ArrowUpRight className="w-3 h-3 text-[#00ff41]" />
        ) : (
          <ArrowDownRight className="w-3 h-3 text-[#ff3b30]" />
        )}
        <span className={`text-[10px] font-mono ${commodity.change >= 0 ? "text-[#00ff41]" : "text-[#ff3b30]"}`}>
          {commodity.change >= 0 ? "+" : ""}{commodity.change.toFixed(2)}%
        </span>
      </div>
    </button>
  );
}
